import type { FC } from 'react'
import {
  ColorPicker as ArkColorPicker,
  type UseColorPickerProps,
  useColorPicker,
} from '@ark-ui/react/color-picker'
import { cn } from '@/utils'

const {
  RootProvider,
  Control,
  Trigger,
  ValueSwatch,
  Positioner,
  Content,
  Area,
  AreaBackground,
  AreaThumb,
  ChannelSlider,
  ChannelSliderTrack,
  ChannelSliderThumb,
  SwatchGroup,
  SwatchTrigger,
  Swatch,
  SwatchIndicator,
  HiddenInput,
} = ArkColorPicker

interface ColorPickerProps extends UseColorPickerProps {
  palette?: string[]
}

export const ColorPicker: FC<ColorPickerProps> = ({
  palette = ['#264653', '#2a9d8f', '#e9c46a', '#f4a261', '#e76f51'],
  ...props
}) => {
  const colorPicker = useColorPicker(props)

  return (
    <div className="flex flex-col gap-6">
      <RootProvider value={colorPicker}>
        <Control className="flex items-center flex-row gap-2 relative rounded focus-within:border-ring focus-within:ring-3 focus-within:ring-offset-1 focus-within:ring-offset-white">
          <Trigger className="rounded transition-opacity ease-in-out duration-300 data-disabled:opacity-40">
            <ValueSwatch className="w-7 h-7 rounded border border-border cursor-pointer transition-colors ease-in-out duration-300" />
          </Trigger>
        </Control>

        <Positioner>
          <Content
            className={cn(
              'flex flex-col bg-gray-700 rounded-2xl shadow-lg z-50 w-64 p-4 gap-3',
              'data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95',
            )}
          >
            {/* Color Area */}
            <Area className="h-44 rounded-lg overflow-hidden">
              <AreaBackground className="h-full" />
              <AreaThumb className="h-3.5 w-3.5 rounded-full border-2 border-white shadow-sm focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75" />
            </Area>

            {/* Channel Sliders */}
            <div className="flex flex-col gap-2">
              <ChannelSlider
                channel="hue"
                className="h-3.5 rounded-lg flex-1"
              >
                <ChannelSliderTrack className="h-3.5 rounded-lg shadow-inner border border-black/10" />
                <ChannelSliderThumb className="h-3.5 w-3.5 rounded-full border-2 border-white shadow-sm -translate-x-1/2 -translate-y-1/2 focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-blue-500" />
              </ChannelSlider>
            </div>

            {/* Preset Color Swatches */}
            <SwatchGroup className="flex items-center justify-center gap-2 flex-row flex-wrap">
              {palette.map((color, index) => (
                <SwatchTrigger
                  className="flex items-center justify-center"
                  key={index}
                  value={color}
                >
                  <Swatch
                    value={color}
                    className={cn(
                      'size-7 rounded cursor-pointer hover:scale-110 transition ease-in-out duration-300',
                      'data-[state=unchecked]:border data-[state=unchecked]:border-gray-200',
                      'data-[state=checked]:rounded-full data-[state=checked]:scale-110',
                    )}
                  >
                    <SwatchIndicator className="text-white rounded-full">
                      ✓
                    </SwatchIndicator>
                  </Swatch>
                </SwatchTrigger>
              ))}
            </SwatchGroup>
          </Content>
        </Positioner>

        <HiddenInput />
      </RootProvider>
    </div>
  )
}
